/**
 * Connect-path NATS-health signal.
 *
 * The NATS heartbeat (`installNatsHeartbeat`) only produces health events once
 * a CLIENT EXISTS — it listens to `client.status()`. A failed (or hung) INITIAL
 * connect never yields a status event, so without this signal the connectivity
 * FSM stays optimistically `CONNECTED` while the very first connection is being
 * rejected + retried. The connect path reports socket health here; the
 * JetStream bootstrap reports wire health here; the connectivity orchestrator
 * folds the derived value into `natsHealthy` alongside the heartbeat.
 *
 * Two components, one derived value:
 *   - `socket` — the WebSocket is up (set true when a connection lands, false
 *     on every connect failure).
 *   - `wire`   — the JetStream consumers for this socket are bound (false after
 *     a failed bootstrap attempt, true once every root is bound). Reset to
 *     true whenever a NEW socket lands, because the wire is rebuilt per client.
 *   derived = socket && wire. A broken wire on a healthy socket is
 *   DEGRADED_NO_NATS: realtime is not flowing, polling should be.
 *
 * The 3 s hysteresis still applies downstream, so a sub-3 s blip that
 * recovers does not flip the user-visible pill.
 *
 * HMR-safe: pinned on `globalThis` so a dev hot-reload doesn't fork the signal.
 * Platform-agnostic — `globalThis` only, no web/RN API.
 */

export type ConnectHealthListener = (healthy: boolean, cause: string) => void;

interface ConnectHealthState {
  socket: boolean;
  wire: boolean;
  cause: string;
  /** Latches true the first time a live socket is confirmed this session.
   *  Lets the UI show "Connecting" until the FIRST real connect, rather than
   *  the optimistic-default CONNECTED. Never resets to false (a later drop is
   *  "Reconnecting", not "Connecting"). */
  hasEverConnected: boolean;
  readonly listeners: Set<ConnectHealthListener>;
}

const PIN_KEY = '__morfi_connectHealth' as const;

type GlobalWithConnectHealth = typeof globalThis & {
  [PIN_KEY]?: ConnectHealthState;
};

function initialState(): ConnectHealthState {
  return {
    socket: true,
    wire: true,
    cause: 'init',
    hasEverConnected: false,
    listeners: new Set(),
  };
}

function store(): ConnectHealthState {
  const g = globalThis as GlobalWithConnectHealth;
  if (g[PIN_KEY] === undefined) g[PIN_KEY] = initialState();
  return g[PIN_KEY];
}

/** Whether a live NATS socket has been established at least once this session. */
export function hasEverConnected(): boolean {
  return store().hasEverConnected;
}

/** Derived connect-path health: socket up AND its JetStream wire bound. */
export function isConnectHealthy(): boolean {
  const s = store();
  return s.socket && s.wire;
}

function notify(s: ConnectHealthState, cause: string): void {
  s.cause = cause;
  const healthy = s.socket && s.wire;
  for (const listener of s.listeners) listener(healthy, cause);
}

/**
 * Report the connect path's view of the SOCKET. Called with `false` on every
 * connect failure (initial + each retry attempt) and `true` once a live socket
 * is established. A new socket resets the wire component: its consumers are
 * about to be (re)built.
 */
export function reportConnectHealth(healthy: boolean, cause: string): void {
  const s = store();
  s.socket = healthy;
  if (healthy) {
    s.hasEverConnected = true;
    s.wire = true;
  }
  notify(s, cause);
}

/**
 * Report the JetStream WIRE on the current socket: `false` after a failed
 * bootstrap attempt (or a reaped consumer that could not be recreated),
 * `true` once every allow-sub root is bound.
 */
export function reportWireHealth(healthy: boolean, cause: string): void {
  const s = store();
  s.wire = healthy;
  notify(s, cause);
}

/**
 * Subscribe to connect-health changes. Immediately replays the current value so
 * a subscriber that installs AFTER a connect has already failed converges to
 * the right state. Returns an unsubscribe fn.
 */
export function subscribeConnectHealth(listener: ConnectHealthListener): () => void {
  const s = store();
  s.listeners.add(listener);
  listener(s.socket && s.wire, s.cause);
  return (): void => {
    s.listeners.delete(listener);
  };
}

/** Test-only — reset the pinned signal. */
export function _resetConnectHealthForTest(): void {
  (globalThis as GlobalWithConnectHealth)[PIN_KEY] = initialState();
}
